import { TFunction } from 'i18next';
import { format } from 'date-fns';
import {
  AzericardPaymentStatusParams,
  Gateways,
  GenericPaymentStatusParams,
  PaymentStatusParams,
  PaymentSuccessStatusData,
} from '@/bridge/types/payment';
import { PaymentService, PaymentStatus } from '@/api/types/payments.types';
import { formatNumber } from '@/utils/helpers';

export const getSuccessModalTitle = (t: TFunction, serviceId = '') => {
  switch (true) {
    case ['add_balance_custom', 'wallet'].includes(serviceId):
      return t('webview.payment_receipt.wallet_and_balance_success_title');
    case serviceId.includes('bump'):
      return t('webview.payment_receipt.success_title');
    case serviceId.includes('featured'):
      return t('webview.payment_receipt.success_title');
    case serviceId.includes('bundle'):
      return t('webview.payment_receipt.ad_success_title');
    case ['pay_invoice'].includes(serviceId):
      return t('webview.payment_receipt.pay_invoice_success_title');
    case ['pin_recovery'].includes(serviceId):
      return t('webview.payment_receipt.pin_recovery_success_title');
    case serviceId.includes('vip'):
      return t('webview.payment_receipt.success_title');
    default:
      return t('webview.payment_receipt.success_title');
  }
};

export const getSuccessModalMessage = (t: TFunction, serviceId = '') => {
  switch (true) {
    case ['add_balance_custom', 'wallet'].includes(serviceId):
      return t('webview.payment_receipt.wallet_and_balance_success_message');
    case serviceId.includes('bump'):
      return t('webview.payment_receipt.bump_success_message');
    case serviceId.includes('featured'):
      return t('webview.payment_receipt.featured_success_message');
    case serviceId.includes('bundle'):
      return t('webview.payment_receipt.ad_success_message');
    case ['pay_invoice'].includes(serviceId):
      return t('webview.payment_receipt.pay_invoice_success_message');
    case serviceId.includes('vip'):
      return t('webview.payment_receipt.vip_success_message');
    default:
      return '';
  }
};

export const getPaymentAmount = (payment: PaymentSuccessStatusData) => {
  if ('gateway' in payment && payment.gateway === Gateways.Azericard) {
    return [payment.amount, payment.currency].filter(Boolean).join(' ');
  }
  return 'amount' in payment ? payment.amount : '';
};

export const getAzericardPaymentEntries = (
  t: TFunction,
  payment: AzericardPaymentStatusParams.Success
): Array<[string, string]> => {
  const data = new Map<string, string>();

  if (payment.order_id) {
    data.set('payment_id', payment.order_id);
  }

  if (payment.rrn) {
    data.set('rrn', payment.rrn);
  }

  data.set('payment_source', t('webview.payment_receipt.payment_source_bank'));

  return Array.from(data.entries());
};

export const getGeneralPaymentEntries = (
  t: TFunction,
  payment: GenericPaymentStatusParams.Success
): Array<[string, string]> => {
  const data = new Map<string, string>();

  if (payment.updated_at) {
    data.set('payment_date', payment.updated_at);
  }

  if (payment.payment_id) {
    data.set('payment_id', payment.payment_id);
  }

  if (payment.service_id !== 'wallet') {
    if (payment.target_id) data.set('target_id', payment.target_id);
  }

  if (payment.service_name) {
    data.set('service_name', payment.service_name);
  }

  if (payment.is_wallet) {
    const isWallet = payment.is_wallet === 'true';

    data.set(
      'payment_source',
      t(
        `webview.payment_receipt.payment_source_${isWallet ? 'wallet' : 'bank'}`
      )
    );
  }

  return Array.from(data.entries());
};

export const getPaymentEntries = (
  t: TFunction,
  payment: PaymentStatusParams
): Array<[string, string]> => {
  if ('gateway' in payment && payment.gateway === Gateways.Azericard) {
    return getAzericardPaymentEntries(
      t,
      payment as AzericardPaymentStatusParams.Success
    );
  }

  return getGeneralPaymentEntries(
    t,
    payment as GenericPaymentStatusParams.Success
  );
};

export const getRowName = (
  t: TFunction,
  name: string,
  isAddBalance: boolean
) => {
  switch (name) {
    case 'payment_date':
      return t('webview.payment_receipt.payment_date');
    case 'payment_id':
      return t('webview.payment_receipt.payment_id');
    case 'service_name':
      return t('webview.payment_receipt.service_name');
    case 'target_id':
      return isAddBalance
        ? t('pages.wallet.private_account')
        : t('webview.payment_receipt.target_id');
    case 'rrn':
      return t('webview.payment_receipt.rrn');
    case 'payment_source':
      return t('webview.payment_receipt.payment_source');
    default:
      return null;
  }
};

export const buildWebViewAddBalancePaymentData = (
  t: TFunction,
  data: {
    paymentDate: string;
    paymentId: string;
    targetId: string;
    amount: string;
  }
): PaymentStatusParams => {
  const { paymentDate, paymentId, targetId, amount } = data;
  return {
    status: PaymentStatus.Success,
    updated_at: format(new Date(paymentDate), 'dd.MM.yyyy, HH:mm'),
    payment_id: paymentId,
    service_id: PaymentService.Add_balance_custom,
    service_name: t('common.add_balance'),
    target_id: targetId,
    is_wallet: 'false',
    amount: `${formatNumber(Number(amount), 2)} AZN`,
    message: '',
    payment_source: t('webview.payment_receipt.payment_source_bank'),
  };
};

export const buildWebViewPaymentData = (
  t: TFunction,
  data: {
    paymentDate: string;
    paymentId: string;
    targetId: string;
    amount: string;
    serviceName: string;
    serviceId: string;
    isWallet: boolean;
  }
): PaymentStatusParams => {
  return {
    status: PaymentStatus.Success,
    updated_at: format(new Date(data.paymentDate), 'dd.MM.yyyy, HH:mm'),
    payment_id: data.paymentId,
    service_id: data.serviceId,
    service_name: data.serviceName,
    target_id: data.targetId,
    is_wallet: data.isWallet ? 'true' : 'false',
    amount: `${formatNumber(Number(data.amount), 2)} AZN`,
    message: '',
    payment_source: t(
      `webview.payment_receipt.payment_source_${
        data.isWallet ? 'wallet' : 'bank'
      }`
    ),
  };
};
